/**
 * Procedural audio synthesis — bakes short AudioBuffers OFFLINE so the game has
 * sound with ZERO asset files. Every manifest entry carries a `synth` spec; the
 * SoundBank renders it once (OfflineAudioContext) into an AudioBuffer that then
 * plays through the EXACT same path a loaded file would. Drop a real file URL on
 * the same manifest entry and it overrides the synth, per-sound, no code change.
 *
 * Specs are small declarative objects (a discriminated union) so adding a sound
 * is data, not code. `renderSynth()` is the only entry point.
 */

export type NoiseColor = "white" | "pink" | "brown";

interface Base {
  /** Start offset within a layered sound (seconds). */
  delay?: number;
  /** Peak linear gain of the whole spec (pre-bus). Default 1. */
  gain?: number;
  /** Render a seamless loop (steady body + wrap crossfade). Default false. */
  loop?: boolean;
}

/** Filtered noise — impacts, whooshes, wind/rain/ocean beds, splashes, steps. */
export interface NoiseSpec extends Base {
  kind: "noise";
  dur: number;
  color?: NoiseColor; // spectral tilt of the source noise
  freq?: number; // filter centre (Hz); omit for full-band
  freqEnd?: number; // sweep the filter to here over `dur`
  q?: number; // filter resonance (bandpass sharpness)
  type?: BiquadFilterType; // default bandpass when freq set, else lowpass
  attack?: number;
  hold?: number;
  decay?: number;
}

/** Oscillator blips — UI beeps, tonal stingers, the body of a chord voice. */
export interface ToneSpec extends Base {
  kind: "tone";
  dur: number;
  freq: number;
  freqEnd?: number; // glide
  type?: OscillatorType;
  attack?: number;
  hold?: number;
  decay?: number;
  detune?: number; // adds a second voice this many cents off → width
  vibrato?: number; // Hz
  vibratoDepth?: number; // cents
}

/** Fast descending square/saw zap — laser/bolt fire. */
export interface ZapSpec extends Base {
  kind: "zap";
  dur: number;
  freq: number;
  freqEnd: number;
  type?: OscillatorType;
}

/** Pitch-dropping sine + click — footfalls, lands, the punch of an explosion. */
export interface ThudSpec extends Base {
  kind: "thud";
  dur: number;
  freq: number;
  freqEnd?: number;
  noise?: number; // 0..1 noise transient mixed on top (grit)
}

/** Sustained detuned chord with a slow filter LFO — music/ambient pads. */
export interface ChordSpec extends Base {
  kind: "chord";
  dur: number;
  root: number; // Hz of the root
  intervals: number[]; // semitone offsets voiced above the root
  type?: OscillatorType;
  lfoRate?: number; // Hz of the brightness sweep (loop tuned to whole cycles)
  bright?: number; // 0..1 base filter openness
  detune?: number; // per-voice spread (cents)
}

/** Sum several specs into one buffer (e.g. boom = thud + noise + sub). */
export interface LayersSpec extends Base {
  kind: "layers";
  dur: number;
  layers: SynthSpec[];
}

export type SynthSpec = NoiseSpec | ToneSpec | ZapSpec | ThudSpec | ChordSpec | LayersSpec;

const semi = (n: number): number => Math.pow(2, n / 12);

/** A reusable noise AudioBuffer (1 channel) in the given offline context. */
function makeNoise(ctx: OfflineAudioContext, dur: number, color: NoiseColor): AudioBuffer {
  const len = Math.max(1, Math.ceil(dur * ctx.sampleRate));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  if (color === "white") {
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  } else if (color === "pink") {
    // Paul Kellet's economical pink filter.
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.969 * b2 + w * 0.153852;
      b3 = 0.8665 * b3 + w * 0.3104856;
      b4 = 0.55 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.016898;
      d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
      b6 = w * 0.115926;
    }
  } else {
    // Brown — integrated white, gently leaked to stay bounded.
    let last = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      last = (last + 0.02 * w) / 1.02;
      d[i] = last * 3.5;
    }
  }
  return buf;
}

/** Linear AD(SR-less) envelope onto a gain param. Loops hold flat at `peak`. */
function env(g: AudioParam, t0: number, dur: number, a: number, h: number, dRel: number, peak: number, loop: boolean): void {
  if (loop) {
    g.setValueAtTime(peak, t0);
    return;
  }
  const atk = Math.min(a, dur);
  g.setValueAtTime(0.0001, t0);
  g.linearRampToValueAtTime(peak, t0 + atk);
  const holdEnd = Math.min(dur, atk + h);
  g.setValueAtTime(peak, t0 + holdEnd);
  g.linearRampToValueAtTime(0.0001, t0 + Math.min(dur, holdEnd + dRel));
}

/** Build a spec's node graph into `dest` at offset time `t`. */
function build(ctx: OfflineAudioContext, spec: SynthSpec, dest: AudioNode, t: number): void {
  t += spec.delay ?? 0;
  const loop = !!spec.loop;
  const gain = spec.gain ?? 1;

  switch (spec.kind) {
    case "noise": {
      const src = ctx.createBufferSource();
      src.buffer = makeNoise(ctx, spec.dur + 0.1, spec.color ?? "white");
      const filt = ctx.createBiquadFilter();
      filt.type = spec.type ?? (spec.freq ? "bandpass" : "lowpass");
      filt.frequency.value = spec.freq ?? 8000;
      if (spec.freqEnd != null && spec.freq != null) {
        filt.frequency.setValueAtTime(spec.freq, t);
        filt.frequency.exponentialRampToValueAtTime(Math.max(20, spec.freqEnd), t + spec.dur);
      }
      filt.Q.value = spec.q ?? 0.7;
      const vca = ctx.createGain();
      env(vca.gain, t, spec.dur, spec.attack ?? 0.004, spec.hold ?? 0, spec.decay ?? spec.dur, gain, loop);
      src.connect(filt).connect(vca).connect(dest);
      src.start(t);
      src.stop(t + spec.dur + 0.05);
      break;
    }
    case "tone": {
      const voices = spec.detune ? [-spec.detune, spec.detune] : [0];
      const vca = ctx.createGain();
      env(vca.gain, t, spec.dur, spec.attack ?? 0.005, spec.hold ?? 0, spec.decay ?? spec.dur, gain / voices.length, loop);
      vca.connect(dest);
      for (const dt of voices) {
        const osc = ctx.createOscillator();
        osc.type = spec.type ?? "sine";
        osc.frequency.setValueAtTime(spec.freq, t);
        if (spec.freqEnd != null) osc.frequency.exponentialRampToValueAtTime(Math.max(20, spec.freqEnd), t + spec.dur);
        osc.detune.value = dt;
        if (spec.vibrato) {
          const lfo = ctx.createOscillator();
          const lg = ctx.createGain();
          lfo.frequency.value = spec.vibrato;
          lg.gain.value = spec.vibratoDepth ?? 8;
          lfo.connect(lg).connect(osc.detune);
          lfo.start(t);
          lfo.stop(t + spec.dur + 0.05);
        }
        osc.connect(vca);
        osc.start(t);
        osc.stop(t + spec.dur + 0.05);
      }
      break;
    }
    case "zap": {
      const osc = ctx.createOscillator();
      osc.type = spec.type ?? "sawtooth";
      osc.frequency.setValueAtTime(spec.freq, t);
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, spec.freqEnd), t + spec.dur);
      const vca = ctx.createGain();
      env(vca.gain, t, spec.dur, 0.001, 0, spec.dur, gain, loop);
      osc.connect(vca).connect(dest);
      osc.start(t);
      osc.stop(t + spec.dur + 0.05);
      break;
    }
    case "thud": {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(spec.freq, t);
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, spec.freqEnd ?? spec.freq * 0.4), t + spec.dur);
      const vca = ctx.createGain();
      env(vca.gain, t, spec.dur, 0.002, 0, spec.dur, gain, loop);
      osc.connect(vca).connect(dest);
      osc.start(t);
      osc.stop(t + spec.dur + 0.05);
      if (spec.noise) {
        const n = ctx.createBufferSource();
        n.buffer = makeNoise(ctx, 0.08, "white");
        const nf = ctx.createBiquadFilter();
        nf.type = "highpass";
        nf.frequency.value = 1200;
        const ng = ctx.createGain();
        env(ng.gain, t, 0.07, 0.001, 0, 0.07, gain * spec.noise, false);
        n.connect(nf).connect(ng).connect(dest);
        n.start(t);
        n.stop(t + 0.1);
      }
      break;
    }
    case "chord": {
      const lfo = ctx.createOscillator();
      const lfoG = ctx.createGain();
      const filt = ctx.createBiquadFilter();
      filt.type = "lowpass";
      const base = 200 + (spec.bright ?? 0.4) * 2600;
      filt.frequency.value = base;
      filt.Q.value = 0.6;
      lfo.frequency.value = spec.lfoRate ?? 0.1;
      lfoG.gain.value = base * 0.6;
      lfo.connect(lfoG).connect(filt.frequency);
      lfo.start(t);
      lfo.stop(t + spec.dur + 0.05);
      const vca = ctx.createGain();
      const n = spec.intervals.length || 1;
      env(vca.gain, t, spec.dur, loop ? 0 : 0.6, 0, loop ? 0 : 1.2, gain / n, loop);
      filt.connect(vca).connect(dest);
      for (const iv of spec.intervals) {
        const spread = spec.detune ?? 6;
        for (const dt of [-spread, spread]) {
          const osc = ctx.createOscillator();
          osc.type = spec.type ?? "sawtooth";
          osc.frequency.value = spec.root * semi(iv);
          osc.detune.value = dt;
          osc.connect(filt);
          osc.start(t);
          osc.stop(t + spec.dur + 0.05);
        }
      }
      break;
    }
    case "layers": {
      const vca = ctx.createGain();
      vca.gain.value = gain;
      vca.connect(dest);
      for (const sub of spec.layers) build(ctx, sub, vca, t);
      break;
    }
  }
}

/** Fold the rendered tail back over the head (equal-power) so a loop has no
 *  seam. Returns a buffer trimmed to exactly `dur`. */
function wrapLoop(rendered: AudioBuffer, dur: number, fade: number): AudioBuffer {
  const sr = rendered.sampleRate;
  const outLen = Math.max(1, Math.floor(dur * sr));
  const fadeLen = Math.min(Math.floor(fade * sr), outLen);
  const Ctor = (globalThis as unknown as { AudioBuffer?: typeof AudioBuffer }).AudioBuffer;
  // OfflineAudioContext can mint a destination buffer for us when AudioBuffer
  // isn't directly constructable in this runtime.
  const out =
    typeof Ctor === "function"
      ? new Ctor({ length: outLen, numberOfChannels: rendered.numberOfChannels, sampleRate: sr })
      : new OfflineAudioContext(rendered.numberOfChannels, outLen, sr).createBuffer(
          rendered.numberOfChannels,
          outLen,
          sr,
        );
  for (let ch = 0; ch < rendered.numberOfChannels; ch++) {
    const src = rendered.getChannelData(ch);
    const dst = out.getChannelData(ch);
    for (let i = 0; i < outLen; i++) dst[i] = src[i];
    // Crossfade the overhang (samples outLen..outLen+fadeLen) into the head.
    for (let i = 0; i < fadeLen; i++) {
      const tail = src[outLen + i] ?? 0;
      const x = i / fadeLen;
      const wIn = Math.sin((x * Math.PI) / 2);
      const wOut = Math.cos((x * Math.PI) / 2);
      dst[i] = dst[i] * wIn + tail * wOut;
    }
  }
  return out;
}

/** Render a synth spec to an AudioBuffer at the given sample rate. */
export async function renderSynth(spec: SynthSpec, sampleRate: number): Promise<AudioBuffer> {
  const loop = !!spec.loop;
  const fade = loop ? Math.min(0.08, spec.dur * 0.5) : 0;
  const renderDur = spec.dur + fade + 0.05;
  const ctx = new OfflineAudioContext(2, Math.max(1, Math.ceil(renderDur * sampleRate)), sampleRate);
  build(ctx, spec, ctx.destination, 0);
  const rendered = await ctx.startRendering();
  return loop ? wrapLoop(rendered, spec.dur, fade) : rendered;
}
